import type { RunReadFailure } from "@/api/previewInsights";
import { QueryErrorState } from "@/api/QueryErrorState";
import { DeniedState, EmptyState, ErrorState, OfflineState } from "@/ui/states";

/**
 * The failure surfaces a Preview Insights read can end on, kept apart the way
 * the contract keeps them apart (#1098; shared with the event reads by #1102).
 * 401 and 501 are delegated to `QueryErrorState`, which owns the sign-in and
 * not-built recoveries for every screen.
 *
 * No "Back to project" here: the run page's header already carries the one
 * way back, and two controls with one name on one screen is the
 * repeated-ambiguous-control defect `navModel.ts` records.
 */
export function PreviewRunReadFailure({
  failure,
  error,
  onRetry,
}: {
  failure: RunReadFailure;
  error: unknown;
  onRetry: () => void;
}) {
  switch (failure.kind) {
    case "denied":
      return (
        <DeniedState
          title="This preview run is not available to your account"
          description="Your account is not authorized to inspect preview runs in this project in this environment. Nothing about the run has been read."
        />
      );
    case "not_found":
      // ONE generic answer, rendered as one generic sentence. The service
      // collapses an unknown locator, another Project's run, another
      // environment's, a binding the target no longer holds and a moved
      // candidate onto this response BY DESIGN, and the browser must not
      // pretend to know which — naming a reason would be inventing one.
      return (
        <EmptyState
          title="Preview run not available"
          description="No preview run is available at this address for your account in this environment. The link may be out of date, the run may belong to a different project or deployment, or its preview target may no longer hold it."
        />
      );
    case "upstream":
      return failure.recovery === "retry" ? (
        <ErrorState
          title="The preview target took too long to answer"
          description="The read did not complete in time. This says nothing about the run itself — the same request may well succeed."
          onRetry={onRetry}
        />
      ) : (
        <ErrorState
          title="The preview target could not answer"
          description="The recorded preview target is unreachable, is not serving Preview Insights in this deployment, or answered something this Editor build cannot validate. This says nothing about the run itself; an identical request is refused until an operator acts, and you can try again after that."
          onRetry={onRetry}
        />
      );
    case "error":
      return (
        <ErrorState
          title="This preview run could not be read"
          description={`The Editor Service answered with "${failure.code}"${
            failure.requestId ? ` (request ${failure.requestId})` : ""
          }.`}
          onRetry={onRetry}
        />
      );
    case "transport":
      // No answer arrived, so nothing here is a statement by the service —
      // the same surface the pending branch renders for a paused query.
      return (
        <OfflineState
          title="The Editor Service could not be reached"
          description="No response arrived for this read. Nothing about the run has been read; check the connection and try again."
          onRetry={onRetry}
        />
      );
    case "unauthenticated":
    case "not_implemented":
      return (
        <QueryErrorState
          error={error}
          subject="Preview runs"
          title="This preview run could not be read"
          onRetry={onRetry}
        />
      );
  }
}
